import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Building2,
  LayoutDashboard,
  LogOut,
  Menu,
  ShieldCheck,
  UserCog,
  X
} from 'lucide-react';

const navigation = [
  { name: 'Overview', href: '/admin/dashboard', icon: LayoutDashboard },
  { name: 'Owners', href: '/admin/owners', icon: UserCog },
  { name: 'Properties', href: '/admin/pgs', icon: Building2 }
];

export default function AdminLayout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const adminProfile = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('admin_profile') || 'null');
    } catch {
      return null;
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_profile');
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#f6f7f2] text-slate-900">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[320px] overflow-hidden">
        <div className="absolute inset-0 bg-dot-grid opacity-45" />
        <div className="absolute left-[-8%] top-[-16%] h-64 w-64 rounded-full bg-emerald-200/35 blur-3xl animate-pulse-soft" />
        <div className="absolute right-[4%] top-[8%] h-56 w-56 rounded-full bg-cyan-200/25 blur-3xl animate-pulse-soft animation-delay-300" />
      </div>

      <div className="relative">
        <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-[#f6f7f2]/90 backdrop-blur-xl">
          <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between gap-4">
              <div className="flex min-w-0 items-center gap-3">
                <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-slate-950 shadow-[0_14px_30px_rgba(15,23,42,0.15)]">
                  <ShieldCheck className="h-5 w-5 text-emerald-300" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-lg font-bold tracking-tight text-slate-950">Solucate Super Admin</p>
                  <p className="truncate text-[11px] uppercase tracking-[0.24em] text-slate-500">
                    {adminProfile?.email || 'Platform Control'}
                  </p>
                </div>
              </div>

              <div className="hidden items-center gap-3 lg:flex">
                <nav className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/90 p-2 shadow-sm">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-all ${
                          isActive
                            ? 'bg-slate-950 text-white'
                            : 'text-slate-600 hover:bg-slate-100 hover:text-slate-950'
                        }`}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>

                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="h-11 rounded-full border-slate-300 bg-white/90 px-5"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>

              <button
                onClick={() => setMenuOpen((open) => !open)}
                className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm lg:hidden"
              >
                {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>

            {menuOpen && (
              <div className="mt-4 rounded-[28px] border border-slate-200 bg-white p-4 shadow-[0_20px_50px_rgba(15,23,42,0.08)] lg:hidden">
                <div className="space-y-2">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMenuOpen(false)}
                        className={`flex items-center gap-3 rounded-2xl px-4 py-3 text-sm font-semibold transition-all ${
                          isActive
                            ? 'bg-slate-950 text-white'
                            : 'text-slate-700 hover:bg-slate-100'
                        }`}
                      >
                        <item.icon className="h-4 w-4" />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>

                <Button
                  onClick={handleLogout}
                  variant="outline"
                  className="mt-4 h-11 w-full rounded-full border-slate-300"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Logout
                </Button>
              </div>
            )}
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 lg:py-10">
          {children}
        </main>
      </div>
    </div>
  );
}
